import { ASYNC_TASK_STATUS, ASYNC_TASK_TYPES, IMPORT_TYPES, IMPORT_TYPES_HISTORY } from "@/other/constants/OtherKeys.jsx";

import { getAsyncNotificationConfig } from "@/modern/components/notification/constants/config";

import { ACCOUNT_LEVEL_ASYNC_TASK_TYPES } from "../constants/scope";
import type {
  AsyncTask,
  AsyncTaskLifecyclePhase,
  AsyncTaskMetaByType,
  AsyncTaskOf,
  AsyncTaskRecordBase,
  AsyncTaskStatus,
  AsyncTaskTypeId,
  ImportDataTypeId,
} from "../types";

// ── Result API envelope ───────────────────────────────────────────────────────

/** New `/async-task/{id}/result` body shape: `{ meta_data, result }`. */
export type AsyncTaskResultEnvelope = {
  meta_data?: Record<string, unknown>;
  result?: unknown;
};

/**
 * Strip the `{ meta_data, result }` envelope so callers receive the bare result.
 * Keeps Blobs (CSV export) and legacy bare JSON bodies as-is.
 */
export function unwrapAsyncTaskResultBody(body: unknown): unknown {
  if (typeof Blob !== "undefined" && body instanceof Blob) return body;
  if (
    body &&
    typeof body === "object" &&
    "result" in body &&
    (body as AsyncTaskResultEnvelope).result !== undefined
  ) {
    return (body as AsyncTaskResultEnvelope).result;
  }
  return body;
}

/**
 * Coerce `/async-task/{id}/result` CSV payloads (Blob or raw string) into a
 * downloadable Blob. Returns null when there is nothing to download.
 */
export function toCsvDownloadBlob(result: unknown): Blob | null {
  if (result == null) return null;
  if (typeof Blob !== "undefined" && result instanceof Blob) return result;
  if (typeof result === "string") {
    return new Blob([result], { type: "text/csv;charset=utf-8" });
  }
  if (typeof result === "object") {
    return new Blob([JSON.stringify(result)], {
      type: "text/csv;charset=utf-8",
    });
  }
  return new Blob([String(result)], { type: "text/csv;charset=utf-8" });
}

// ── Display messages ──────────────────────────────────────────────────────────

export function getTaskStatusMessage(task: AsyncTask): string {
  const config = getAsyncNotificationConfig(task.type);

  switch (task.status) {
    case "Pending":
      return config?.getPendingMessage(task) ?? "Working…";
    case "Completed":
      return config?.getCompletedMessage?.(task) ?? "Complete";
    case "Resolved":
      return (
        config?.getResolvedMessage?.(task) ??
        config?.getCompletedMessage?.(task) ??
        "Resolved"
      );
    case "Failed":
      return config?.getFailedMessage?.(task) ?? "Failed";
    case "Cancelled":
      return "Cancelled";
  }
}

// ── Normalize ─────────────────────────────────────────────────────────────────

export function normalizeMeta<T extends AsyncTaskTypeId>(
  type: T,
  meta: unknown,
): AsyncTaskMetaByType[T] {
  const m = { ...(meta as Record<string, unknown>) };

  if (
    (type === ASYNC_TASK_TYPES.BOARD_CONTEXT_QUESTIONS_GENERATION ||
      type === ASYNC_TASK_TYPES.BOARD_CONTEXT_GENERATION) &&
    m.board_context_id == null &&
    m.context_id != null
  ) {
    m.board_context_id = m.context_id;
  }

  return m as AsyncTaskMetaByType[T];
}

// ── Parse ─────────────────────────────────────────────────────────────────────

const ASYNC_TASK_TYPE_IDS = new Set<number>(Object.values(ASYNC_TASK_TYPES));

export function isKnownAsyncTaskTypeId(type: number): type is AsyncTaskTypeId {
  return ASYNC_TASK_TYPE_IDS.has(type);
}

export function parseAsyncTaskStatus(status: string): AsyncTaskStatus | null {
  if (status === ASYNC_TASK_STATUS.PENDING) return "Pending";
  if (status === ASYNC_TASK_STATUS.COMPLETED) return "Completed";
  if (status === ASYNC_TASK_STATUS.RESOLVED) return "Resolved";
  if (status === ASYNC_TASK_STATUS.FAILED) return "Failed";
  if (status === ASYNC_TASK_STATUS.CANCELLED) return "Cancelled";
  return null;
}

export function parseAsyncTask(raw: AsyncTaskRecordBase): AsyncTask | null {
  if (!raw?.type || raw.meta_data == null) return null;
  if (!isKnownAsyncTaskTypeId(raw.type)) return null;

  const status = parseAsyncTaskStatus(raw.status);
  if (!status) return null;

  const type = raw.type;
  const meta = normalizeMeta(type, raw.meta_data);

  return {
    ...raw,
    type,
    status,
    meta_data: meta,
  } as AsyncTask;
}

export function statusToLifecyclePhase(
  status: AsyncTaskStatus,
): AsyncTaskLifecyclePhase {
  switch (status) {
    case "Pending":
      return "pending";
    case "Completed":
      return "completed";
    case "Resolved":
      return "resolved";
    case "Failed":
      return "failed";
    case "Cancelled":
      return "cancelled";
  }
}

// ── Guards ────────────────────────────────────────────────────────────────────

export function isAsyncTaskType<T extends AsyncTaskTypeId>(
  task: AsyncTask,
  type: T,
): task is AsyncTaskOf<T> {
  return task.type === type;
}

/**
 * Terminal success statuses that the UI groups under the "Completed" bucket.
 * `Resolved` is a settled/historical variant of `Completed` emitted by the backend.
 */
export function isTerminalCompletedStatus(
  status: AsyncTaskStatus | null | undefined,
): boolean {
  return status === "Completed" || status === "Resolved";
}

// ── Filters ───────────────────────────────────────────────────────────────────

export function matchAccountLevelTask(task: AsyncTask): boolean {
  return (ACCOUNT_LEVEL_ASYNC_TASK_TYPES as readonly number[]).includes(task.type);
}

export function matchBoardTask(task: AsyncTask, boardId: number): boolean {
  const meta = task.meta_data as { board_id?: number };
  return meta.board_id === boardId;
}

/** Board export (type 7) — API meta may only include `board_name` after poll. */
export function matchBoardExportTask(
  task: AsyncTask,
  boardId: number,
  boardName?: string,
): boolean {
  if (!isAsyncTaskType(task, ASYNC_TASK_TYPES.BOARD_EXPORT)) return false;

  const meta = task.meta_data as { board_id?: number; board_name?: string };
  if (meta.board_id != null && meta.board_id === boardId) return true;

  const normalizedName = boardName?.trim();
  if (normalizedName && meta.board_name?.trim() === normalizedName) return true;

  return false;
}

export type BoardContextTaskPhase =
  | "idle"
  | "analysing"
  | "building"
  | "suggesting";

const BOARD_CONTEXT_ASYNC_TASK_TYPES = [
  ASYNC_TASK_TYPES.BOARD_CONTEXT_QUESTIONS_GENERATION,
  ASYNC_TASK_TYPES.BOARD_CONTEXT_GENERATION,
  ASYNC_TASK_TYPES.BOARD_CONTEXT_SUGGESTION,
] as const;

export function matchBoardContextBoardTask(
  task: AsyncTask,
  boardId: number,
): boolean {
  if (!matchBoardTask(task, boardId)) return false;

  return (BOARD_CONTEXT_ASYNC_TASK_TYPES as readonly number[]).includes(task.type);
}

export function matchBoardContextTask(
  task: AsyncTask,
  boardId: number,
  contextId: number,
): boolean {
  const meta = task.meta_data as {
    board_id?: number;
    board_context_id?: number;
  };
  return meta.board_id === boardId && meta.board_context_id === contextId;
}

export function deriveBoardContextPhase(
  tasks: AsyncTask[],
  boardId: number,
  contextId: number,
): BoardContextTaskPhase {
  const pending = tasks.filter(
    (task) =>
      task.status === "Pending" &&
      matchBoardContextTask(task, boardId, contextId),
  );

  if (
    pending.some((task) =>
      isAsyncTaskType(task, ASYNC_TASK_TYPES.BOARD_CONTEXT_GENERATION),
    )
  ) {
    return "building";
  }

  if (
    pending.some((task) =>
      isAsyncTaskType(task, ASYNC_TASK_TYPES.BOARD_CONTEXT_QUESTIONS_GENERATION),
    )
  ) {
    return "analysing";
  }

  if (
    pending.some((task) =>
      isAsyncTaskType(task, ASYNC_TASK_TYPES.BOARD_CONTEXT_SUGGESTION),
    )
  ) {
    return "suggesting";
  }

  return "idle";
}

export function matchWorkFileTask(
  task: AsyncTask,
  boardId: number,
  workFileId: number,
): boolean {
  const meta = task.meta_data as { board_id?: number; work_file_id?: number };
  return meta.board_id === boardId && meta.work_file_id === workFileId;
}

/** Best-effort parse of async task id from action API response bodies. */
export function extractAsyncTaskId(data: unknown): number | null {
  if (data == null || typeof data !== "object") return null;

  const record = data as Record<string, unknown>;
  const nested =
    record.async_task != null && typeof record.async_task === "object"
      ? (record.async_task as Record<string, unknown>)
      : null;

  const candidates = [
    record.async_task_id,
    record.asyncTaskId,
    record.task_id,
    nested?.id,
    record.id,
  ];

  for (const candidate of candidates) {
    const parsed =
      typeof candidate === "string" ? Number.parseInt(candidate, 10) : candidate;
    if (typeof parsed === "number" && Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return null;
}

export function matchImportTask(
  task: AsyncTask,
  boardId: number,
  dataType?: ImportDataTypeId,
): boolean {
  if (
    task.type !== ASYNC_TASK_TYPES.CSV_IMPORT &&
    task.type !== ASYNC_TASK_TYPES.UNDO_CSV_IMPORT
  ) {
    return false;
  }

  const meta = task.meta_data as {
    board_id?: number;
    data_type?: ImportDataTypeId;
  };

  if (meta.board_id !== boardId) return false;
  if (dataType == null) return true;
  return meta.data_type === dataType;
}

/** Maps import editor `record.importType` to async-task `data_type` (IMPORT_TYPES_HISTORY). */
export function resolveImportDataTypeId(
  importType: string,
): ImportDataTypeId | null {
  const historyKey =
    importType === IMPORT_TYPES.taskBudget
      ? "tasks-budget"
      : importType === IMPORT_TYPES.boardUsers
        ? "board-users"
        : importType;

  const id =
    IMPORT_TYPES_HISTORY[historyKey as keyof typeof IMPORT_TYPES_HISTORY];
  return id != null ? (id as ImportDataTypeId) : null;
}

/** All board-scoped CSV import, export, and undo tasks (any data type). */
export function matchBoardCsvImportExportTasks(
  task: AsyncTask,
  boardId: number,
): boolean {
  if (!matchBoardTask(task, boardId)) return false;

  return (
    task.type === ASYNC_TASK_TYPES.CSV_IMPORT ||
    task.type === ASYNC_TASK_TYPES.CSV_EXPORT ||
    task.type === ASYNC_TASK_TYPES.UNDO_CSV_IMPORT
  );
}

/** Board-scoped marketing manifest and tags generation (types 10, 11). */
export function matchBoardMarketingGeneratorTask(
  task: AsyncTask,
  boardId: number,
): boolean {
  if (!matchBoardTask(task, boardId)) return false;

  return (
    task.type === ASYNC_TASK_TYPES.MARKETING_MANIFEST_GENERATION ||
    task.type === ASYNC_TASK_TYPES.MARKETING_TAGS_GENERATION
  );
}

/** Board-scoped marketing work file generation (type 12). */
export function matchBoardMarketingWorkFileGenerationTask(
  task: AsyncTask,
  boardId: number,
  coverageId?: number,
): boolean {
  if (!matchBoardTask(task, boardId)) return false;
  if (task.type !== ASYNC_TASK_TYPES.MARKETING_WORK_FILE_GENERATION) return false;

  if (coverageId == null) return true;

  const meta = task.meta_data as { coverage_id?: number };
  return meta.coverage_id == null || meta.coverage_id === coverageId;
}

/** Board-scoped marketing page generation (type 13). */
export function matchBoardMarketingPageGenerationTask(
  task: AsyncTask,
  boardId: number,
  workFileId?: number,
): boolean {
  if (!matchBoardTask(task, boardId)) return false;
  if (task.type !== ASYNC_TASK_TYPES.MARKETING_PAGE_GENERATION) return false;

  if (workFileId == null) return true;

  const meta = task.meta_data as { work_file_id?: number };
  return meta.work_file_id == null || meta.work_file_id === workFileId;
}

/** Board-scoped marketing document generation (type 19). */
export function matchBoardMarketingDocumentGenerationTask(
  task: AsyncTask,
  boardId: number,
  workFileId?: number,
): boolean {
  if (!matchBoardTask(task, boardId)) return false;
  if (task.type !== ASYNC_TASK_TYPES.MARKETING_DOCUMENT_GENERATION) return false;

  if (workFileId == null) return true;

  const meta = task.meta_data as { work_file_id?: number };
  return meta.work_file_id == null || meta.work_file_id === workFileId;
}

/** Board-scoped outcome incentive evaluation (type 8). */
export function matchBoardIncentiveEvaluationTask(
  task: AsyncTask,
  boardId: number,
): boolean {
  return (
    matchBoardTask(task, boardId) &&
    task.type === ASYNC_TASK_TYPES.INCENTIVE_EVALUATION
  );
}

export interface IncentiveEvaluationTaskResult {
  incentive_id?: number;
  points_created?: number;
}

/** Resolve outcome incentive records from completed evaluation tasks (type 8). */
export async function resolveIncentiveEvaluationOutcomeRecords(
  tasks: AsyncTask[],
  fetchResult: (taskId: number) => Promise<unknown>,
  project: { outcomeIncentiveStore: { getById: (id: number) => { id: number; name?: string; points?: number; createdAt?: Date | string } | undefined } },
): Promise<{
  records: NonNullable<ReturnType<typeof project.outcomeIncentiveStore.getById>>[];
  taskIds: number[];
  emptyTaskIds: number[];
}> {
  const records: NonNullable<ReturnType<typeof project.outcomeIncentiveStore.getById>>[] = [];
  const taskIds: number[] = [];
  const emptyTaskIds: number[] = [];
  const seenIncentiveIds = new Set<number>();

  for (const task of tasks) {
    try {
      const data = (await fetchResult(task.id)) as IncentiveEvaluationTaskResult;
      const pointsCreated = data?.points_created ?? 0;
      const meta = task.meta_data as { incentive_id?: number } | undefined;
      const incentiveId = data?.incentive_id ?? meta?.incentive_id;

      if (incentiveId == null) {
        emptyTaskIds.push(task.id);
        continue;
      }

      const record = project.outcomeIncentiveStore.getById(incentiveId);
      if (!record || seenIncentiveIds.has(incentiveId)) {
        emptyTaskIds.push(task.id);
        continue;
      }

      // Skip truly empty evaluations (no points created and store shows none).
      const storePoints = Number(record.points ?? 0);
      if (pointsCreated <= 0 && storePoints <= 0) {
        emptyTaskIds.push(task.id);
        continue;
      }

      seenIncentiveIds.add(incentiveId);
      records.push(record);
      taskIds.push(task.id);
    } catch {
      emptyTaskIds.push(task.id);
    }
  }

  records.sort(
    (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime(),
  );

  return { records, taskIds, emptyTaskIds };
}

/** Board-scoped site publish and custom domain deploy (types 17, 18). */
export function matchBoardSiteAsyncTask(
  task: AsyncTask,
  boardId: number,
): boolean {
  if (!matchBoardTask(task, boardId)) return false;

  return (
    task.type === ASYNC_TASK_TYPES.SITE_PUBLISH ||
    task.type === ASYNC_TASK_TYPES.SITE_CUSTOM_DOMAIN_DEPLOY
  );
}

/** Board-scoped CSV import, export, and undo tasks for the import editor modal. */
export function matchImportEditorCsvTask(
  task: AsyncTask,
  boardId: number,
  dataTypeId: ImportDataTypeId,
): boolean {
  if (!matchBoardTask(task, boardId)) return false;

  if (task.type === ASYNC_TASK_TYPES.CSV_EXPORT) {
    const meta = task.meta_data as { data_type?: ImportDataTypeId };
    return meta.data_type === dataTypeId;
  }

  if (
    task.type === ASYNC_TASK_TYPES.CSV_IMPORT ||
    task.type === ASYNC_TASK_TYPES.UNDO_CSV_IMPORT
  ) {
    return matchImportTask(task, boardId, dataTypeId);
  }

  return false;
}
